import { datadogLogs } from "@datadog/browser-logs";
import CONFIGS from "@/core/utils/configs";
import type { TelemetryStage } from "@/core/telemetry.types";

export type { TelemetryStage } from "@/core/telemetry.types";

const MAX_EVENTS_PER_SESSION = 50;
const EVENT_COUNT_KEY = "teams-telemetry-event-count";

let enabled = false;
let fallbackEventCount = 0;

const countEvent = () => {
  try {
    const count = Number(sessionStorage.getItem(EVENT_COUNT_KEY)) + 1;
    sessionStorage.setItem(EVENT_COUNT_KEY, String(count));
    return count;
  } catch {
    fallbackEventCount += 1;
    return fallbackEventCount;
  }
};

const withoutFragment = (url: string) => url.split("#")[0];

export const initTelemetry = () => {
  if (enabled || !CONFIGS.DATADOG_CLIENT_TOKEN) return;

  datadogLogs.init({
    clientToken: CONFIGS.DATADOG_CLIENT_TOKEN,
    site: CONFIGS.DATADOG_SITE,
    service: "teams-app",
    env: CONFIGS.ENVIRONMENT,
    sessionSampleRate: 100,
    sessionPersistence: ["local-storage", "cookie", "memory"],
    forwardErrorsToLogs: true,
    forwardConsoleLogs: ["error"],
    beforeSend: (event) => {
      event.view.url = withoutFragment(event.view.url);
      return countEvent() <= MAX_EVENTS_PER_SESSION;
    },
  });
  enabled = true;
};

export const setTelemetryUser = (username: string) => {
  if (!enabled || !username) return;
  datadogLogs.setUser({ id: username });
};

export const setTelemetryCompany = (
  id: number,
  name: string,
  apiDomain: string,
) => {
  if (!enabled) return;
  datadogLogs.setGlobalContextProperty("company", { id, name, apiDomain });
};

export const reportTelemetryFailure = (
  stage: TelemetryStage,
  context?: Record<string, unknown>,
) => {
  if (!enabled) return;
  datadogLogs.logger.error(`Teams shell stopped at ${stage}`, {
    stage,
    ...context,
  });
};

export const reportTelemetryError = (
  error: unknown,
  context?: Record<string, unknown>,
) => {
  const normalized = error instanceof Error ? error : new Error(String(error));
  if (!enabled) {
    console.error(normalized, context);
    return;
  }
  datadogLogs.logger.error(normalized.message, context, normalized);
};
